/**
 * Subscriptions API service - handles subscription and billing API calls
 */

import { apiClient } from './client';
import type {
  SubscriptionPlanResponse,
  SubscriptionResponse,
  CurrentSubscriptionResponse,
  CheckoutRequest,
  CheckoutResponse,
  PortalResponse,
  SubscriptionUpgradeRequest,
  InvoiceListResponse,
  PaymentResponse,
} from '@neokanban/types';

/**
 * Subscriptions API methods
 */
export const subscriptionsApi = {
  /**
   * Get current subscription with usage information
   */
  async getCurrentSubscription(): Promise<CurrentSubscriptionResponse> {
    const response = await apiClient.get<any>('/subscriptions/current');
    return transformCurrentSubscription(response.data);
  },

  /**
   * Get all available subscription plans (public endpoint)
   */
  async listPlans(): Promise<SubscriptionPlanResponse[]> {
    const response = await apiClient.get<any>('/subscriptions/plans');
    return response.data.map(transformPlan);
  },

  /**
   * Create a Stripe checkout session to start or upgrade subscription
   */
  async createCheckoutSession(data: CheckoutRequest): Promise<CheckoutResponse> {
    const response = await apiClient.post<any>('/subscriptions/checkout', {
      plan_id: data.planId,
      billing_period: data.billingPeriod,
      success_url: data.successUrl,
      cancel_url: data.cancelUrl,
    });
    return {
      checkoutUrl: response.data.checkoutUrl ?? response.data.checkout_url,
      sessionId: response.data.sessionId ?? response.data.session_id,
    };
  },

  /**
   * Upgrade subscription to a new plan (takes effect immediately)
   */
  async upgradeSubscription(data: SubscriptionUpgradeRequest): Promise<SubscriptionResponse> {
    const response = await apiClient.post<any>('/subscriptions/upgrade', {
      plan_id: data.planId,
      billing_period: data.billingPeriod,
    });
    return transformSubscription(response.data);
  },

  /**
   * Cancel subscription (default: at period end, can cancel immediately)
   */
  async cancelSubscription(cancelImmediately: boolean = false): Promise<SubscriptionResponse> {
    const response = await apiClient.post<any>(
      `/subscriptions/cancel?cancel_immediately=${cancelImmediately}`
    );
    return transformSubscription(response.data);
  },

  /**
   * Reactivate a subscription that was set to cancel at period end
   */
  async reactivateSubscription(): Promise<SubscriptionResponse> {
    const response = await apiClient.post<any>('/subscriptions/reactivate');
    return transformSubscription(response.data);
  },

  /**
   * Get Stripe customer portal URL for managing payment methods
   */
  async getCustomerPortal(): Promise<PortalResponse> {
    const response = await apiClient.get<any>('/subscriptions/portal');
    return {
      portalUrl: response.data.portalUrl ?? response.data.portal_url,
    };
  },

  /**
   * Get billing history (past invoices)
   */
  async listInvoices(limit: number = 10): Promise<InvoiceListResponse> {
    const response = await apiClient.get<any>(`/subscriptions/invoices?limit=${limit}`);
    return {
      invoices: response.data.invoices.map(transformPayment),
      total: response.data.total,
    };
  },
};

/**
 * Transform API response to camelCase
 * Backend uses alias_generator=to_camel, but we handle both formats
 */
function transformPlan(data: any): SubscriptionPlanResponse {
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    priceMonthly: data.priceMonthly ?? data.price_monthly,
    priceYearly: data.priceYearly ?? data.price_yearly,
    trialDays: data.trialDays ?? data.trial_days,
    isActive: data.isActive ?? data.is_active,
    features: data.features,
    limits: data.limits,
    stripeProductId: data.stripeProductId ?? data.stripe_product_id,
    stripePriceIdMonthly: data.stripePriceIdMonthly ?? data.stripe_price_id_monthly,
    stripePriceIdYearly: data.stripePriceIdYearly ?? data.stripe_price_id_yearly,
    yearlySavingsPercent: data.yearlySavingsPercent ?? data.yearly_savings_percent,
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
}

function transformSubscription(data: any): SubscriptionResponse {
  return {
    id: data.id,
    organizationId: data.organizationId ?? data.organization_id,
    planId: data.planId ?? data.plan_id,
    plan: transformPlan(data.plan),
    status: data.status,
    billingPeriod: data.billingPeriod ?? data.billing_period,
    stripeCustomerId: data.stripeCustomerId ?? data.stripe_customer_id,
    stripeSubscriptionId: data.stripeSubscriptionId ?? data.stripe_subscription_id,
    currentPeriodStart: data.currentPeriodStart ?? data.current_period_start,
    currentPeriodEnd: data.currentPeriodEnd ?? data.current_period_end,
    trialEnd: data.trialEnd ?? data.trial_end,
    cancelAtPeriodEnd: data.cancelAtPeriodEnd ?? data.cancel_at_period_end,
    canceledAt: data.canceledAt ?? data.canceled_at,
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
}

function transformPayment(data: any): PaymentResponse {
  return {
    id: data.id,
    organizationId: data.organizationId ?? data.organization_id,
    subscriptionId: data.subscriptionId ?? data.subscription_id,
    amount: data.amount,
    currency: data.currency,
    status: data.status,
    stripePaymentIntentId: data.stripePaymentIntentId ?? data.stripe_payment_intent_id,
    stripeInvoiceId: data.stripeInvoiceId ?? data.stripe_invoice_id,
    invoiceUrl: data.invoiceUrl ?? data.invoice_url,
    failureReason: data.failureReason ?? data.failure_reason,
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
}

function transformCurrentSubscription(data: any): CurrentSubscriptionResponse {
  return {
    subscription: data.subscription ? transformSubscription(data.subscription) : null,
    usage: data.usage,
    canUpgrade: data.canUpgrade ?? data.can_upgrade,
    availablePlans: data.availablePlans?.map(transformPlan) ?? data.available_plans?.map(transformPlan) ?? [],
  };
}

export default subscriptionsApi;
